import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import '../../../elements/katapult-elements/katapult-button.js';
import { capitalCase } from 'change-case';
import { PaperTableStyle } from '../../../elements/style-modules/paper-table-style.js';
import '../../../elements/katapult-elements/katapult-icon.js';
import { map } from 'lit/directives/map.js';
import { createRef, ref } from 'lit/directives/ref.js';
import '../../../elements/katapult-elements/katapult-dialog.js';
import '../../../elements/katapult-drop-down/katapult-drop-down.js';
import { ShouldUpdate } from '../../../modules/ShouldUpdate.js';
import { SetItemData } from '../katapult-custom-function-editor-step.js';

const DEFAULT_NAMING_CONVENTION = {
  integer: {
    connectionTypes: ['aerial cable', 'slack span', 'overlash']
  },
  alphabetical: {
    connectionTypes: ['reference', 'pushbrace', 'underground cable']
  },
  decimal: {
    connectionTypes: ['pole to pole guy', 'overhead guy']
  },
  none: {
    connectionTypes: ['down guy']
  }
};
export class OrderToolEditorStep extends LitElement {
  static get properties() {
    return {
      toolItemData: { type: Object },
      otherAttributes: { type: Object },
      _allConnectionTypes: { type: Array, state: true },
      _namingConventions: { type: Array },
      _validConnectionTypes: { type: Object },
      _formattedNamingConvention: { type: Object },
      _editingNamingConvention: { type: String },
      _selectedConnectionType: { type: Object }
    };
  }

  static styles = [
    unsafeCSS(PaperTableStyle),
    css`
      #container {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
      }
      .namingTable {
        width: 20em;
        margin: var(--sl-spacing-small);
      }
      .connectionTypeLabel {
        text-overflow: ellipsis;
        overflow: hidden;
        max-height: -webkit-fill-available;
        text-wrap-mode: nowrap;
      }
    `
  ];

  constructor() {
    super();

    /** @type { SetItemData } */
    this.setItemData = SetItemData.bind(this);

    // comes from katapult-custom-function-editor-step.js
    this.toolItemData = {};
    this.otherAttributes = {};

    // local state properties that don't affect UI
    this._allConnectionTypes = [];

    // local state properties that affect UI
    this._namingConventions = [
      {
        label: 'Integer',
        value: 'integer',
        example: '(001, 002, 003)'
      },
      {
        label: 'Alphabetical',
        value: 'alphabetical',
        example: '(001.A, 001.B, 001.C)'
      },
      {
        label: 'Decimal',
        value: 'decimal',
        example: '(001.1, 001.2, 001.3)'
      },
      {
        label: 'None',
        value: 'none',
        example: '(Do not order)'
      }
    ];
    this._validConnectionTypes = [];
    this._formattedNamingConvention = {};
    this._editingNamingConvention = null;
    this._selectedConnectionType = null;
    this._addConnectionTypeDialog = createRef();
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('otherAttributes')) this.compileConnectionTypes();
    if (['toolItemData', '_allConnectionTypes'].some((property) => changedProperties.has(property))) this.setDefaultNamingConvention();
    if (changedProperties.has('toolItemData')) {
      this.setValidConnectionTypes();
      this.formatNamingConvention();
    }
  }

  shouldUpdate(changedProperties) {
    return ShouldUpdate(this, changedProperties);
  }

  render() {
    const hasValidConnectionTypes = this._validConnectionTypes.length > 0;
    return html`
      <katapult-dialog title="Add Connection Type" ${ref(this._addConnectionTypeDialog)} maxwidth="400">
        <span style="margin-bottom: var(--sl-spacing-small)">
          Select a connection type to add to the ${this._editingNamingConvention?.label} naming convention</span
        >
        ${hasValidConnectionTypes
          ? html`
              <katapult-drop-down
                .items=${this._validConnectionTypes}
                label="Select a connection type"
                label-path="label"
                value-path="value"
                value=${this._selectedConnectionType}
                @selected-changed=${(e) => (this._selectedConnectionType = e.detail.value)}
                no-label-float
              ></katapult-drop-down>
            `
          : nothing}
        ${!hasValidConnectionTypes ? html` <span><i>All connection types are currently assigned!</i></span> ` : nothing}
        <div slot="buttons" style="display: flex; justify-content: flex-end;">
          <katapult-button dialog-dismiss style="margin-right: var(--sl-spacing-small)">Cancel</katapult-button>
          <katapult-button
            color="var(--secondary-color"
            @click=${() => this.addConnectionType(this._editingNamingConvention, this._selectedConnectionType)}
            ?disabled=${!hasValidConnectionTypes || !this._selectedConnectionType}
            >Add</katapult-button
          >
        </div>
      </katapult-dialog>
      <div id="container">
        ${map(this._namingConventions, (namingConvention) => {
          const { label: namingConventionLabel, value: namingConventionKey, example: namingConventionExample } = namingConvention;
          const labelWithExample = [namingConventionLabel, namingConventionExample].filter((x) => x).join(' ');
          const connectionTypes = this._formattedNamingConvention[namingConventionKey]?.connectionTypes ?? [];
          return html`
            <paper-table rounded outline class="namingTable">
              <paper-row grey top-rounded><paper-cell>${labelWithExample}</paper-cell></paper-row>
              <div style="display: flex; flex-grow: 1; overflow-y: auto; flex-direction: column;">
                ${connectionTypes.length != 0
                  ? html`
                      ${map(
                        connectionTypes,
                        (connectionType) => html`
                          <paper-row divider>
                            <paper-cell><span class="connectionTypeLabel">${connectionType.label}</span></paper-cell>
                            <paper-cell icon>
                              <katapult-icon
                                icon="delete"
                                style="cursor: pointer"
                                @click=${() => this.deleteConnectionType(namingConventionKey, connectionType.value)}
                              ></katapult-icon>
                            </paper-cell>
                          </paper-row>
                        `
                      )}
                    `
                  : nothing}
                ${connectionTypes.length == 0
                  ? html`
                      <div style="display: flex; justify-content: center; align-items: center; flex-grow: 1;">
                        <span style="padding: 2em;">This list is empty</span>
                      </div>
                    `
                  : nothing}
              </div>
              <paper-row>
                <paper-cell
                  style="justify-content: center; cursor: pointer; font-size: 13px;"
                  @click=${() => this.openAddConnectionTypeDialog(namingConvention)}
                >
                  <katapult-icon
                    icon="add_circle_outline"
                    outline
                    style="margin-right: var(--sl-spacing-small); color: grey"
                  ></katapult-icon>
                  <span style="color: grey">ADD CONNECTION TYPE</span>
                </paper-cell>
              </paper-row>
            </paper-table>
          `;
        })}
      </div>
    `;
  }

  compileConnectionTypes() {
    if (!this.otherAttributes) return;

    // compile all the picklists into a single array of valid connection types
    const allConnectionTypes = [];
    const connectionTypePicklists = this.otherAttributes?.connection_type?.picklists ?? {};
    for (const picklistArray of Object.values(connectionTypePicklists)) {
      for (const picklistEntry of picklistArray) {
        // determine if the connection type already exists in the list
        const connectionTypeValue = picklistEntry.value;
        const valueAlreadyExists = allConnectionTypes.find((connectionType) => connectionType.value == connectionTypeValue);
        if (valueAlreadyExists) continue;

        // add the connection type to the list
        const connectionTypeLabel = this.getLabelForConnectionType(connectionTypeValue);
        const connectionType = {
          value: picklistEntry.value,
          label: connectionTypeLabel
        };
        allConnectionTypes.push(connectionType);
      }
    }
    this._allConnectionTypes = allConnectionTypes;
  }

  setValidConnectionTypes() {
    // filter out connection types that are already in the current naming convention
    const allConnectionTypes = this._allConnectionTypes ?? [];
    const currentNamingConvention = this.toolItemData?.naming_convention;
    const validConnectionTypes = this.getUniqueConnectionTypesFromNamingConvention(allConnectionTypes, currentNamingConvention);
    this._validConnectionTypes = [...validConnectionTypes];
  }

  getLabelForConnectionType(connectionTypeValue) {
    if (!connectionTypeValue) return;
    return capitalCase(connectionTypeValue);
  }

  setDefaultNamingConvention() {
    if (this.toolItemData != null && this.toolItemData?.naming_convention) return;

    this.setItemData('naming_convention', DEFAULT_NAMING_CONVENTION);
  }

  getUniqueConnectionTypesFromNamingConvention(connectionTypes, namingConvention) {
    if (!connectionTypes || !namingConvention) return [];
    const uniqueConnectionTypes = connectionTypes.filter((connectionType) => {
      const connectionTypeValue = connectionType.value;
      const connectionTypeExists = Object.values(namingConvention).some((namingConvention) =>
        namingConvention.connectionTypes.includes(connectionTypeValue)
      );
      return !connectionTypeExists;
    });
    return uniqueConnectionTypes;
  }

  formatNamingConvention() {
    const unformattedNamingConvention = structuredClone(this.toolItemData?.naming_convention ?? {});
    const namingConvention = {};
    for (const [namingConventionKey, namingConventionData] of Object.entries(unformattedNamingConvention)) {
      // format the connection types
      const connectionTypes = namingConventionData.connectionTypes;
      const formattedConnectionTypes = connectionTypes.map((connectionType) => {
        const connectionTypeLabel = this.getLabelForConnectionType(connectionType);
        return {
          value: connectionType,
          label: connectionTypeLabel
        };
      });
      namingConvention[namingConventionKey] = { connectionTypes: formattedConnectionTypes };
    }

    // set the naming convention data
    this._formattedNamingConvention = { ...namingConvention };
  }

  openAddConnectionTypeDialog(namingConventionKey) {
    this._editingNamingConvention = namingConventionKey;
    this._addConnectionTypeDialog.value?.open();
  }

  addConnectionType(namingConvention, connectionType) {
    this._addConnectionTypeDialog.value?.close();
    this._selectedConnectionType = null;

    // create a copy of the tool item data
    const toolItemData = structuredClone(this.toolItemData ?? {});
    const toolNamingConvention = toolItemData.naming_convention;
    const namingConventionKey = namingConvention?.value;
    if ([namingConventionKey, connectionType, toolNamingConvention].some((prop) => prop == null)) return;

    // add the connection type to the list
    if (!toolNamingConvention[namingConventionKey]) toolNamingConvention[namingConventionKey] = { connectionTypes: [] };
    toolNamingConvention[namingConventionKey].connectionTypes.push(connectionType);

    this.setItemData(toolItemData);
  }

  deleteConnectionType(namingConventionKey, connectionTypeToDelete) {
    // get all data necessary to delete the connection type
    const toolItemData = structuredClone(this.toolItemData ?? {});
    const connectionTypesForNamingConvention = toolItemData.naming_convention?.[namingConventionKey]?.connectionTypes;
    if ([namingConventionKey, connectionTypeToDelete, connectionTypesForNamingConvention].some((prop) => prop == null)) return;

    // find the index of the connection type to delete
    const indexOfConnectionTypeToDelete = connectionTypesForNamingConvention.findIndex(
      (toolDataConnectionType) => toolDataConnectionType == connectionTypeToDelete
    );
    if (indexOfConnectionTypeToDelete == -1) return;

    // remove the connection type from the list
    connectionTypesForNamingConvention.splice(indexOfConnectionTypeToDelete, 1);

    this.setItemData(toolItemData);
  }
}
customElements.define('order-tool-editor-step', OrderToolEditorStep);
